import React, { useContext, useState } from 'react';
import { cloneDeep } from 'lodash';
import { assign } from 'xstate';
import { Box, Slider, Typography } from '@mui/material';
import { IntlShape } from 'react-intl';

import { SendContext } from '../../SendContext';
import { BaseComponent } from '../Component';
import { MachineContext } from '../../MachineContext';
import { getStringValuesParam, translateWithFallback } from '../../util';
import { EvoachButton } from '../Button';

export interface ScaleInputComponent extends BaseComponent {
  type: 'scaleInput';
  props: ScaleInputProps;
}
export interface ScaleInputProps {
  buttonText: string;
  scaleName?: string;
  saveResultTo: string;
  scale?: number;
  scaleSize?: number;
  keyTexts?: string[];
}
export const ScaleInput: React.FC<ScaleInputProps> = ({
  buttonText,
  scaleName,
  scale,
  saveResultTo,
  scaleSize,
  keyTexts,
}) => {
  const { send } = useContext(SendContext);
  const marksSize =
    scaleSize && typeof scaleSize === 'number' && !Number.isNaN(scaleSize)
      ? scaleSize
      : 10;

  const marks = Array.from(Array(marksSize).keys()).map((index) => {
    return { value: index + 1, label: index + 1 };
  });
  const currentscale = scale ? scale : Math.round(marksSize / 2);
  const [sliderValue, setSliderValue] = useState(currentscale);

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    setSliderValue(newValue as number);
  };

  return (
    <>
      <Typography variant="subtitle1">{scaleName}</Typography>
      <Box sx={{ paddingLeft: '5px', marginBottom: '15px' }}>
        <Slider
          value={sliderValue}
          step={1}
          valueLabelDisplay="off"
          marks={marks}
          min={1}
          max={marksSize}
          onChange={handleSliderChange}
          size="medium"
        />
      </Box>
      <EvoachButton
        onClick={() => {
          send({
            type: 'writeToContext',
            payload: { [saveResultTo]: sliderValue },
          });
          if (keyTexts !== undefined) {
            if (sliderValue > 0 && sliderValue <= keyTexts.length) {
              send({
                type: keyTexts[sliderValue - 1],
              });
            } else {
              send({
                type: 'next',
              });
            }
          } else {
            send({
              type: 'next',
            });
          }
        }}
      >
        {buttonText}
      </EvoachButton>
    </>
  );
};

export const renderScaleInputAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;
    const getValueFrom = payload.getValueFrom;
    const varsForString: Record<string, any> | undefined = getStringValuesParam(
      payload,
      context
    );
    const scalePreset = getValueFrom
      ? parseInt(context.userData[getValueFrom])
      : parseInt(payload.scale + '');

    newContext.widgetData.push({
      type: 'scaleInput',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        buttonText: translateWithFallback(
          intl,
          payload.buttonText,
          varsForString
        ),
        scaleName: translateWithFallback(
          intl,
          payload.scaleName,
          varsForString
        ),
        keyTexts:
          payload.keyTexts && Array.isArray(payload.keyTexts)
            ? payload.keyTexts
            : undefined,
        scaleSize: parseInt(payload.scaleSize + ''),
        scale: scalePreset,
        saveResultTo: payload.saveResultTo,
      },
    });
    return newContext;
  });
};
